import React from 'react';

import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import { SALESFORCE_FIELD_MAX_LENGTH, audMoney, exceedsFieldLength } from '../constants';

import ImportNameField from './ImportNameField';
import type { ImportNameEdits, RrpOf } from './excelImportModel';
import type { ParsedInclusion } from './types';

type ImportInclusionsStepProps = {
  currency: string;
  inclusions: Array<ParsedInclusion>;
  leftOutRows: ReadonlySet<number>;
  nameEdits: ImportNameEdits;
  onNameChange: (key: string, name: string) => void;
  onToggle: (row: number, included: boolean) => void;
  reusesAsset: (inclusionName: string) => boolean;
  rrpNotLoaded: string | null;
  rrpOf: RrpOf | null;
};

export default function ImportInclusionsStep({
  currency,
  inclusions,
  leftOutRows,
  nameEdits,
  onNameChange,
  onToggle,
  reusesAsset,
  rrpNotLoaded,
  rrpOf,
}: ImportInclusionsStepProps) {
  const rrpTextByRow = new Map(
    inclusions.map((inclusion) => {
      const rrp = rrpOf?.(inclusion) ?? null;
      return [inclusion.row, rrp === null ? '' : currencyFormatter(currency, rrp, 2)];
    }),
  );
  const missingRrp = [...rrpTextByRow.values()].includes('');
  return (
    <>
      <Typography color="text.secondary" variant="body2">
        {`Untick inclusions that don't belong to the base packages. The rest load onto every Option 1 package: narrow them, and fill in Type${missingRrp ? ' and RRP' : ''}, on Inclusions afterwards.${rrpNotLoaded ? ` ${rrpNotLoaded}` : ''}`}
      </Typography>
      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Inclusion</TableCell>
            <TableCell>PRV</TableCell>
            <TableCell>RRP</TableCell>
            <TableCell>Asset</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {inclusions.map(({ row, name, prv }) => (
            <TableRow key={row}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={!leftOutRows.has(row)}
                  inputProps={{ 'aria-label': name }}
                  onChange={(event) => onToggle(row, event.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 280 }}>
                {exceedsFieldLength(name, SALESFORCE_FIELD_MAX_LENGTH.inclusionName) ? (
                  <ImportNameField
                    disabled={leftOutRows.has(row)}
                    inputProps={{ 'aria-label': `Shorter name for ${name}` }}
                    kind="inclusion"
                    nameEdits={nameEdits}
                    onNameChange={onNameChange}
                    original={name}
                  />
                ) : (
                  name
                )}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{prv === null ? '' : audMoney(prv)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{rrpTextByRow.get(row)}</TableCell>
              <TableCell>{reusesAsset(name) ? 'Existing asset' : 'New asset'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
